'use client'

import { type ReactNode, useId, useState } from 'react'
import { cn, Input, Slider } from '@nwwp/ui-shadcn'
import { NumberInput } from '~/components/NumberInput'
import { parseCurrency } from '~/utils/currency-format'

const LABEL = 'text-body-sm-bold text-charcoal'
const HINT = 'mt-2 text-body-xs text-neutral-darker'
const THUMB = '[&_[data-slot=slider-thumb]]:size-5'

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n))
}

interface FieldShellProps {
  label: ReactNode
  htmlFor: string
  hint?: ReactNode
  aside?: ReactNode
  children: ReactNode
}

function FieldShell({ label, htmlFor, hint, aside, children }: FieldShellProps) {
  return (
    <div>
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <label htmlFor={htmlFor} className={LABEL}>
          {label}
        </label>
        {aside}
      </div>
      {children}
      {hint && <p className={HINT}>{hint}</p>}
    </div>
  )
}

interface CurrencyFieldProps {
  label: ReactNode
  value: number
  onChange: (value: number) => void
  hint?: ReactNode
  children?: ReactNode
}

export function CurrencyField({ label, value, onChange, hint, children }: CurrencyFieldProps) {
  const id = useId()
  return (
    <FieldShell label={label} htmlFor={id} hint={hint}>
      <NumberInput
        id={id}
        prefix="$"
        value={value}
        decimalScale={0}
        allowNegative={false}
        onChange={(e) => onChange(parseCurrency(e.target.value))}
        className="font-semibold tabular-nums"
      />
      {children}
    </FieldShell>
  )
}

interface StepperFieldProps {
  label: ReactNode
  /** Plain-text name used in the +/- button labels. */
  name: string
  value: number
  min: number
  max: number
  step: number
  onChange: (value: number) => void
  /** Maps the stored value to the number shown in the box (e.g. 0.08 → 8). */
  toDisplay?: (value: number) => number
  fromDisplay?: (display: number) => number
  suffix?: string
  decimals?: number
}

export function StepperField({
  label,
  name,
  value,
  min,
  max,
  step,
  onChange,
  toDisplay = (v) => v,
  fromDisplay = (v) => v,
  suffix,
  decimals = 0,
}: StepperFieldProps) {
  const id = useId()
  const [draft, setDraft] = useState<string | null>(null)
  const shown = toDisplay(value)

  const commit = (display: number) => {
    const snapped = Math.round(display / step) * step
    onChange(fromDisplay(Number(clamp(snapped, min, max).toFixed(decimals))))
  }

  const stepButton = cn(
    'flex size-11 shrink-0 items-center justify-center rounded-full',
    'border border-smoke bg-white text-body-bold text-charcoal',
    'transition-colors hover:border-green hover:text-green-dark',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green',
    'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-smoke disabled:hover:text-charcoal'
  )

  return (
    <FieldShell label={label} htmlFor={id}>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={stepButton}
          aria-label={`Decrease ${name}`}
          disabled={shown <= min}
          onClick={() => commit(shown - step)}
        >
          <span aria-hidden="true" className="text-body-bold">
            −
          </span>
        </button>
        <div className="relative flex-1">
          <Input
            id={id}
            type="text"
            inputMode={decimals > 0 ? 'decimal' : 'numeric'}
            value={draft ?? shown.toFixed(decimals)}
            onFocus={(e) => {
              setDraft(shown.toFixed(decimals))
              e.target.select()
            }}
            onChange={(e) => setDraft(e.target.value.replace(/[^0-9.]/g, ''))}
            onBlur={() => {
              const parsed = parseFloat(draft ?? '')
              if (!Number.isNaN(parsed)) commit(parsed)
              setDraft(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur()
            }}
            className={cn('text-center font-semibold tabular-nums', suffix && 'pr-8')}
          />
          {suffix && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 text-body text-neutral-darker"
            >
              {suffix}
            </span>
          )}
        </div>
        <button
          type="button"
          className={stepButton}
          aria-label={`Increase ${name}`}
          disabled={shown >= max}
          onClick={() => commit(shown + step)}
        >
          <span aria-hidden="true" className="text-body-bold">
            +
          </span>
        </button>
      </div>
    </FieldShell>
  )
}

interface SliderFieldProps {
  label: ReactNode
  name: string
  value: number
  min: number
  max: number
  step: number
  onChange: (value: number) => void
  display: string
  hint?: ReactNode
}

export function SliderField({ label, name, value, min, max, step, onChange, display, hint }: SliderFieldProps) {
  const id = useId()
  return (
    <FieldShell
      label={label}
      htmlFor={id}
      hint={hint}
      aside={<span className="text-body-sm-bold text-green-darker tabular-nums">{display}</span>}
    >
      <InlineSlider
        id={id}
        name={name}
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={onChange}
        className="mt-0"
      />
    </FieldShell>
  )
}

interface InlineSliderProps {
  id?: string
  name: string
  value: number
  min: number
  max: number
  step: number
  onChange: (value: number) => void
  /** Replaces the default `mt-4` offset used when the slider sits under its own input. */
  className?: string
}

/** Slider companion for a field that already has its own label and input. */
export function InlineSlider({ id, name, value, min, max, step, onChange, className = 'mt-4' }: InlineSliderProps) {
  return (
    <Slider
      id={id}
      aria-label={name}
      min={min}
      max={max}
      step={step}
      value={[clamp(value, min, max)]}
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      onValueChange={(values) => onChange(values[0]!)}
      className={cn('py-3', className, THUMB)}
    />
  )
}

const RETIREMENT_AGE_PRESETS = [55, 60, 62, 65, 67] as const
const MAX_RETIREMENT_AGE = 85

interface RetirementAgePickerProps {
  age: number
  value: number
  onChange: (value: number) => void
}

export function RetirementAgePicker({ age, value, onChange }: RetirementAgePickerProps) {
  const inputId = useId()
  const labelId = useId()
  const isPreset = (RETIREMENT_AGE_PRESETS as readonly number[]).includes(value)
  const [customOpen, setCustomOpen] = useState(!isPreset)
  const [draft, setDraft] = useState<string | null>(null)
  const showCustom = customOpen || !isPreset

  const chip = (selected: boolean) =>
    cn(
      'min-h-11 min-w-14 rounded-full border px-4 text-body-sm-bold tabular-nums transition-colors',
      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green',
      'disabled:cursor-not-allowed disabled:opacity-40',
      selected
        ? 'border-green-darker bg-green-darker text-white'
        : 'border-smoke bg-white text-charcoal hover:border-green hover:text-green-dark'
    )

  const commitCustom = () => {
    const parsed = parseInt(draft ?? '', 10)
    if (!Number.isNaN(parsed)) onChange(clamp(parsed, age + 1, MAX_RETIREMENT_AGE))
    setDraft(null)
  }

  return (
    <div>
      <p id={labelId} className={cn(LABEL, 'mb-3')}>
        Retirement age
      </p>
      <div role="group" aria-labelledby={labelId} className="grid grid-cols-3 gap-2 tablet:flex tablet:flex-wrap">
        {RETIREMENT_AGE_PRESETS.map((preset) => {
          const selected = !showCustom && value === preset
          return (
            <button
              key={preset}
              type="button"
              aria-pressed={selected}
              disabled={preset <= age}
              className={chip(selected)}
              onClick={() => {
                setCustomOpen(false)
                onChange(preset)
              }}
            >
              {preset}
            </button>
          )
        })}
        <button
          type="button"
          aria-pressed={showCustom}
          className={chip(showCustom)}
          onClick={() => setCustomOpen(true)}
        >
          Other
        </button>
      </div>
      {showCustom && (
        <div className="mt-3 flex items-center gap-3">
          <label htmlFor={inputId} className="text-body-sm text-neutral-darker">
            Custom age
          </label>
          <Input
            id={inputId}
            type="text"
            inputMode="numeric"
            value={draft ?? String(value)}
            onFocus={(e) => {
              setDraft(String(value))
              e.target.select()
            }}
            onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
            onBlur={commitCustom}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur()
            }}
            className="w-24 text-center font-semibold tabular-nums"
          />
        </div>
      )}
    </div>
  )
}
